import { RespondentProgress } from 'model/respondentProgress'
import { useCallback, useState } from 'react'
import { useOptionalQuestionnaireParams } from './useQuestionnaireParams'

export const getRespondentProgressKey = (
  respondentId: string,
  surveyId: string
) => `${respondentId}.${surveyId}.respondentProgress`

export const clearPersistedRespondentProgress = (
  respondentId: string,
  surveyId: string
) => {
  window.localStorage.removeItem(
    getRespondentProgressKey(respondentId, surveyId)
  )
}

// Preview keeps progress in memory only. Persisting it would leak preview
// state (notably `isTermsAgreed: true`, seeded on mount) into a later real
// visit that reuses the same rid/sid key — hand-edited preview links do this —
// making that respondent silently skip the info/terms pages.
const useRespondentProgress = () => {
  const questionnaireParams = useOptionalQuestionnaireParams()

  const [respondentProgress, setRespondentProgress] = useState(() => {
    const respondentProgressString =
      questionnaireParams && !questionnaireParams.preview
        ? window.localStorage.getItem(
            getRespondentProgressKey(
              questionnaireParams.respondentId,
              questionnaireParams.surveyId
            )
          )
        : null

    return respondentProgressString
      ? (JSON.parse(respondentProgressString) as RespondentProgress)
      : undefined
  })

  const setRespondentProgressInternal = useCallback(
    (newProgress: RespondentProgress) => {
      if (questionnaireParams && !questionnaireParams.preview) {
        window.localStorage.setItem(
          getRespondentProgressKey(
            questionnaireParams.respondentId,
            questionnaireParams.surveyId
          ),
          JSON.stringify(newProgress)
        )
      }
      setRespondentProgress(newProgress)
    },
    [questionnaireParams]
  )

  return {
    respondentProgress,
    setRespondentProgress: setRespondentProgressInternal
  }
}

export default useRespondentProgress
